import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { CheckCircle, XCircle } from 'lucide-react';

export default function VerifyEmail() {
  const { darkMode } = useDarkMode();
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'idle' | 'confirmed' | 'error'>(
    token ? 'idle' : 'error',
  );
  const [errorMessage, setErrorMessage] = useState(
    token ? '' : 'Kein Verifizierungstoken vorhanden',
  );
  const verifying = useRef(false);

  const handleConfirm = async () => {
    if (!token || verifying.current) return;
    verifying.current = true;
    try {
      await api.post<{ message: string }>('/api/auth/verify-email', { token });
      await refreshUser().catch(() => {});
      setStatus('confirmed');
      const redirectTo = localStorage.getItem('postVerifyRedirect') || '/';
      localStorage.removeItem('postVerifyRedirect');
      setTimeout(() => navigate(redirectTo), 2000);
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err.message || 'Verifizierung fehlgeschlagen');
    }
  };

  useEffect(() => {
    if (!token) return;
    const t = setTimeout(handleConfirm, 1000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const bg = darkMode ? 'bg-slate-900' : 'bg-slate-50';
  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const heading = darkMode ? 'text-white' : 'text-slate-900';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-12 ${bg}`}>
      <div className={`w-full max-w-md rounded-2xl border shadow-lg p-8 text-center ${card}`}>

        {status === 'confirmed' && (
          <>
            <div className="flex justify-center mb-4">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <h1 className={`text-2xl font-bold mb-6 ${heading}`}>E-Mail bestätigen</h1>
            <div className="w-full py-2.5 px-6 bg-green-500 text-white font-semibold rounded-lg flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" />
              E-Mail bestätigt
            </div>
          </>
        )}

        {status === 'idle' && (
          <>
            <h1 className={`text-2xl font-bold mb-6 ${heading}`}>E-Mail bestätigen</h1>
            <div className={`w-full py-2.5 px-6 rounded-lg text-sm ${muted}`}>
              Einen Moment…
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center mb-4">
              <XCircle className="w-16 h-16 text-red-500" />
            </div>
            <h1 className={`text-2xl font-bold mb-2 ${heading}`}>Verifizierung fehlgeschlagen</h1>
            <p className={`mb-6 ${muted}`}>{errorMessage}</p>
            <Link
              to="/login"
              className="inline-block py-2.5 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Zur Anmeldung
            </Link>
          </>
        )}

      </div>
    </div>
  );
}
